import { StateCreator } from 'zustand';
import { DocgenClient } from '../../data/docgenClient';
import { handleErrorWithServerMessage } from '../../data/errorHandler';
import { DocumentModel, DocumentPostPayload } from '../../data/interfaces/Document';
import { t } from '../../utils';
import { storeResetFns, useAppStore } from '.';

type State = {
	document: DocumentModel;
	selectedComponent: any;
};

type Actions = {
	createDocument: (templateRID: string) => Promise<DocumentModel>;
	getDocument: (id?: string) => Promise<DocumentModel>;
	updateDocument: (data: Partial<DocumentModel> | DocumentModel) => void;
	setSelectedComponent: (component: any) => void;
	resetDocumentData: () => void;
};

export type DocumentState = State & Actions;

const initialState = {
	document: null,
	selectedComponent: null,
};

export const createDocumentSlice: StateCreator<DocumentState> = (set, get) => {
	storeResetFns.add(() => {
		set(initialState);
	});
	return {
		...initialState,
		createDocument: async templateRID => {
			const { templateMetadata } = useAppStore.getState();
			const payload: DocumentPostPayload = {
				documentTemplateRID: templateRID,
			};
			if (templateMetadata && templateMetadata.containerRID) {
				payload.containerRID = templateMetadata.containerRID;
			}

			try {
				const createDocumentResponse = await DocgenClient.createDocument(payload);
				set({
					document: createDocumentResponse,
				});
				return createDocumentResponse;
			} catch {
				handleErrorWithServerMessage(t('errorMessages.createDocumentError'));
			}
		},
		getDocument: async rid => {
			const { document } = get();

			const documentRID = rid || document?.rid;

			try {
				// eslint-disable-next-line @typescript-eslint/no-shadow
				const document = await DocgenClient.getDocumentbyRID(documentRID);

				if (document) {
					set({
						document,
					});
					return document;
				}
			} catch {
				handleErrorWithServerMessage(t('errorMessages.getDocumentError'));
			}
		},
		updateDocument: (data: Partial<DocumentModel> | DocumentModel) => {
			const { document } = get();
			set({
				document: { ...document, ...data },
			});
		},
		setSelectedComponent(component) {
			set({
				selectedComponent: component,
			});
		},
		resetDocumentData: () => {
			set(initialState);
		},
	};
};
